import { useState } from "react";
import { Button, ButtonVariant, Text } from "@merge-api/merge-javascript-shared";
import { Dialog } from "@merge-api/merge-javascript-shared/dist/designSystem/molecules/Dialog";
import { Select } from "../../components/ConductorFormFields";
import { AccountSyncStatus, SPEED_OPTIONS, speedLabel } from "../types";
import SpeedField from "./SpeedField";

interface Props {
  status: AccountSyncStatus;
  draftSpeed: string;
  onDraftSpeedChange: (speed: string) => void;
  isSaving: boolean;
  onSetOverride: () => void;
  onClearOverride: () => void;
}

const AccountSpeedCard = ({
  status,
  draftSpeed,
  onDraftSpeedChange,
  isSaving,
  onSetOverride,
  onClearOverride,
}: Props) => {
  const [confirmClear, setConfirmClear] = useState(false);
  const hasOverride = status.speed_override !== null;

  // Which layer produced effective_speed. Conductor resolves account override,
  // then org override, then policy, so the badge follows that same order.
  const source = hasOverride
    ? "account override"
    : status.organization_speed_override !== null
    ? "org override"
    : "org entitlement";

  // What clearing would leave behind. Predicted for the confirm dialog only;
  // the value shown afterwards still comes from conductor's response.
  const fallbackSpeed = status.organization_speed_override ?? status.speed;
  const fallbackSource =
    status.organization_speed_override !== null
      ? "the organization override"
      : "the organization entitlement";

  return (
    <div className="h-full rounded-lg border border-gray-10 bg-white">
      <div className="border-b border-gray-10 p-4">
        <Text variant="h6">{status.linked_account_name || "Unnamed linked account"}</Text>
      </div>

      <div className="border-b border-gray-10 p-6">
        <Text variant="sm" className="block text-gray-60">
          Conductor syncs this account
        </Text>
        <Text variant="h2" className="mt-1 block">
          {speedLabel(status.effective_speed)}
        </Text>
        <Text variant="sm" className="mt-1 block text-gray-40">
          from {source}
        </Text>
      </div>

      {/* The layers conductor resolves through, in precedence order. */}
      <div className="grid grid-cols-3 gap-4 border-b border-gray-10 p-4">
        <SpeedField label="Account override">
          <Text variant="md" className={hasOverride ? "text-gray-80" : "text-gray-40"}>
            {hasOverride ? speedLabel(status.speed_override as string) : "None"}
          </Text>
        </SpeedField>
        <SpeedField label="Org override">
          <Text
            variant="md"
            className={status.organization_speed_override ? "text-gray-80" : "text-gray-40"}
          >
            {status.organization_speed_override
              ? speedLabel(status.organization_speed_override)
              : "None"}
          </Text>
        </SpeedField>
        <SpeedField label="Org entitlement">
          <Text variant="md" className="text-gray-80">
            {speedLabel(status.speed)}
          </Text>
        </SpeedField>
      </div>

      <div className="grid grid-cols-3 gap-4 border-b border-gray-10 p-4">
        <SpeedField label="Status">
          <Text variant="md" className="text-gray-80">
            {status.status || "-"}
          </Text>
        </SpeedField>
        <SpeedField label="Category">
          <Text variant="md" className="text-gray-80">
            {status.category || "-"}
          </Text>
        </SpeedField>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4 p-4">
        <div className="flex items-end gap-2">
          <div className="w-40">
            <Text variant="sm" className="mb-1 block text-gray-60">
              Override speed
            </Text>
            <Select value={draftSpeed} onChange={onDraftSpeedChange} disabled={isSaving}>
              <option value="">Select a speed…</option>
              {SPEED_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          <Button
            size="md"
            onClick={onSetOverride}
            disabled={isSaving || draftSpeed === "" || draftSpeed === status.speed_override}
          >
            Set override
          </Button>
        </div>
        <Button
          size="md"
          variant={ButtonVariant.SecondaryGray}
          onClick={() => setConfirmClear(true)}
          disabled={isSaving || !hasOverride}
        >
          Clear account override
        </Button>
      </div>

      <Dialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        variant="sm"
        title="Clear account override?"
        primaryButtonText="Clear override"
        primaryButtonVariant={ButtonVariant.DangerFilled}
        primaryButtonLoading={isSaving}
        onPrimaryButtonClick={() => {
          setConfirmClear(false);
          onClearOverride();
        }}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setConfirmClear(false)}
      >
        <Text variant="md" className="text-gray-70">
          This account will sync <b>{speedLabel(fallbackSpeed)}</b>, from {fallbackSource}.
        </Text>
      </Dialog>
    </div>
  );
};

export default AccountSpeedCard;
